import { ValidationErrorType } from 'hooks/useForm';

type ValidationRules<T> = {
    [key in keyof T]?: {
        required?: {
            message: string;
        };
        isNumber?: {
            message: string;
        };
        isPositive?: {
            message: string;
        };
        isZeroOrHiger?: {
            message: string;
        };
        custom?: {
            validator: (value: string, allValues: T) => boolean;
            message: string;
        }[];
    };
};

function isMissing(value: unknown) {
    return value === null || value === undefined || (typeof value === 'string' && value.trim() === '');
}

export const validateConfigFields = <T extends { [key: string]: string | number }>(
    configs: T[],
    idField: string,
    fieldsToValidate: (keyof T)[],
    validationRules: ValidationRules<T>,
): Record<string, ValidationErrorType> => {
    const newErrors: Record<string, ValidationErrorType> = {};

    configs.forEach((config) => {
        fieldsToValidate.forEach((field) => {
            const fieldKey = `${String(field)}-${config[idField]}`;
            const value = config[field];
            const rules = validationRules[field];

            if (!rules) return;

            // Required validation
            if (rules.required && isMissing(value)) {
                newErrors[fieldKey] = { errorMessage: rules.required.message };
                return;
            }

            // Number validation
            if (rules.isNumber && value?.toString().trim() && isNaN(Number(value))) {
                newErrors[fieldKey] = { errorMessage: rules.isNumber.message };
                return;
            }

            // Positive number validation
            if (rules.isPositive && value?.toString().trim() && Number(value) <= 0) {
                newErrors[fieldKey] = { errorMessage: rules.isPositive.message };
                return;
            }

            if (rules.isZeroOrHiger && value?.toString().trim() && Number(value) < 0) {
                newErrors[fieldKey] = { errorMessage: rules.isZeroOrHiger.message };
                return;
            }

            // Custom validations
            if (rules.custom) {
                for (const customRule of rules.custom) {
                    if (!customRule.validator(value.toString(), config)) {
                        newErrors[fieldKey] = { errorMessage: customRule.message };
                        break;
                    }
                }
            }
        });
    });

    return newErrors;
};
